/**
 * Authentication API service
 */

import { API_BASE_URL, API_ENDPOINTS, isCapacitor } from '../api/config';
import { fetchWithDeadline } from '../api/fetchDeadline';
import { getMainDomainOrigin } from '../utils';
import { emitBackendUnavailable, emitRequestSuccess } from '../api/axiosInstance';
import { getValidAccessToken } from '../api/axiosInstance';
import { translateErrorReference } from '../utils/errors';
import { localizeErrorStatusParams } from '../utils/errorStatusLabels';
import type {
  AuthSessionResponse,
  LoginApiResult,
  LoginRequest,
  LoginResponse,
  MfaChallengeResponse,
  MfaMethod,
  MfaStatus,
  StepUpChallenge,
  StepUpProof,
  MfaTrustedDevice,
  RegisterRequest,
  RegisterResponse,
  RegisterWithInvitationRequest,
  VerifyEmailRequest,
  VerifyEmailResponse,
  ResendVerificationRequest,
  ResendVerificationResponse,
  CheckRegistrationRequest,
  CheckRegistrationResponse,
  RegistrationOption,
  UpdateUserData,
  User } from './types';

interface ApiResponse<T> {
  data: T;
}

class AuthApiError extends Error {
  code: string;
  details?: Record<string, string>;

  constructor(code: string, message: string, details?: Record<string, string>) {
    super(message);
    this.code = code;
    this.details = details;
    this.name = 'AuthApiError';
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  // Check for HTML responses (e.g., WAF 403 pages) before attempting JSON parse
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('text/html')) {
    const text = await response.text();
    console.error('[Auth API] Received HTML instead of JSON:', {
      status: response.status,
      url: response.url,
      body: text.substring(0, 200),
    });
    throw new AuthApiError(
      'HTML_RESPONSE',
      `Server returned HTML (status ${response.status}) instead of JSON. This may indicate a WAF or proxy issue.`,
    );
  }

  const data = await response.json();

  if (!response.ok) {
    // Handle both FastAPI format (detail) and legacy format (error)
    const errorInfo = data.detail || data.error;
    const code = errorInfo?.code || 'UNKNOWN_ERROR';
    const details = errorInfo?.details || {};

    // Build interpolation values from details
    // Map common backend parameters to i18n placeholders. Prefer the server's
    // own timestamp (ISO-8601 UTC, minted next to error_id) so the reference the
    // user quotes matches the log line; the browser clock is a last resort for
    // failures that never reached the server. See errors.ts / error-handling-design.md.
    const localTimestamp = `${new Date().toISOString().slice(0, 19)}Z`;

    // Translate the error code. `translateErrorReference` supplies errorId +
    // timestamp unconditionally so UNEXPECTED_ERROR/INTERNAL_SERVER_ERROR
    // interpolate, and takes the id-LESS arm of those keys when the response
    // carried no `error_id` — a login answered by a proxy or WAF rather than by
    // the backend has none, and the id-bearing copy renders "Reference:  at
    // 2026-08-24T19:23:58Z" on the sign-in screen.
    const translatedMessage = translateErrorReference(`errors:${code}`, {
      errorId: errorInfo?.error_id,
      timestamp: errorInfo?.timestamp ?? localTimestamp,
      // Enum-valued params (`status`, `po_status`) arrive as the raw column
      // token, so they get the same localization the other two translate seams
      // apply (`shared/utils/errorStatusLabels.ts`). No auth code declares one
      // today; this is here so that stays true rather than being the one seam
      // that regresses when one does.
      params: localizeErrorStatusParams(code, details),
      defaultValue: errorInfo?.message || 'An error occurred',
    });

    throw new AuthApiError(
      code,
      translatedMessage,
      details
    );
  }

  return (data as ApiResponse<T>).data;
}

function getAuthHeaders(token?: string | null): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-PanLuma-Client': isCapacitor ? 'mobile' : 'web',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Wrapper around fetch that emits backend unavailable/success events.
 *
 * The single transport for every auth call — login, register and the `/auth/me`
 * bootstrap — so the shared deadline is applied once, here. These stay on
 * `fetch` deliberately (the bootstrap must not reach the axios interceptors),
 * which is why they need `fetchWithDeadline` rather than `axiosInstance`'s
 * `timeout`. Without it a backend that accepted the connection and then stalled
 * left this promise pending forever and the sign-in button spinning.
 */
async function authFetch(url: string, options: RequestInit): Promise<Response> {
  const response = await fetchWithDeadline(url, options);

  // Check for 503 (backend scaled to zero in cloud)
  if (response.status === 503) {
    emitBackendUnavailable();
  } else {
    emitRequestSuccess();
  }

  return response;
}

export const authApi = {
  async login(credentials: LoginRequest): Promise<LoginApiResult> {
    const response = await authFetch(`${API_BASE_URL}${API_ENDPOINTS.auth.login}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(credentials) });
    return handleResponse<LoginApiResult>(response);
  },

  // --- Multi-factor authentication ---
  // Challenge routes (no JWT — gated by the login-issued challenge_token).
  async mfaVerify(data: {
    challenge_token: string;
    code: string;
    remember_device?: boolean;
    timezone?: string;
  }): Promise<LoginResponse> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/mfa/verify`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data) });
    return handleResponse<LoginResponse>(response);
  },

  async mfaResendOtp(challengeToken: string): Promise<{ message_code: string }> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/mfa/resend-otp`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ challenge_token: challengeToken }) });
    return handleResponse<{ message_code: string }>(response);
  },

  // Dev-only escape hatch: completes login by skipping the second factor when
  // running locally. The backend re-checks the non-production + localhost gate,
  // so this 403s in production regardless of the request.
  async mfaLocalhostExempt(data: {
    challenge_token: string;
    timezone?: string;
  }): Promise<LoginResponse> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/mfa/localhost-exempt`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data) });
    return handleResponse<LoginResponse>(response);
  },

  async mfaEnrollLoginStart(data: {
    challenge_token: string;
    method: MfaMethod;
  }): Promise<{
    method: MfaMethod;
    secret?: string;
    provisioning_uri?: string;
    challenge_token?: string;
  }> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/mfa/enroll-login/start`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data) });
    return handleResponse(response);
  },

  async mfaEnrollLoginConfirm(data: {
    challenge_token: string;
    code: string;
    remember_device?: boolean;
    timezone?: string;
  }): Promise<LoginResponse> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/mfa/enroll-login/confirm`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data) });
    return handleResponse<LoginResponse>(response);
  },

  // Settings routes (JWT-authenticated).
  async mfaStatus(token: string): Promise<MfaStatus> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/mfa/status`, {
      method: 'GET',
      headers: getAuthHeaders(token) });
    return handleResponse<MfaStatus>(response);
  },

  async stepUpStart(action: string): Promise<StepUpChallenge> {
    const token = await getValidAccessToken();
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/mfa/step-up/start`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify({ action }),
    });
    return handleResponse<StepUpChallenge>(response);
  },

  async stepUpVerify(data: {
    challenge_token: string;
    credential: string;
  }): Promise<StepUpProof> {
    const token = await getValidAccessToken();
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/mfa/step-up/verify`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data),
    });
    return handleResponse<StepUpProof>(response);
  },

  async mfaEnrollStart(
    token: string,
    password?: string,
  ): Promise<{ method: MfaMethod; secret: string; provisioning_uri: string }> {
    // Password is required only when switching authenticators on an already-enrolled
    // account (server-side step-up re-auth); a first-time enrollment sends none.
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/mfa/enroll/start`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(password ? { password } : {}) });
    return handleResponse(response);
  },

  async mfaEnrollConfirm(
    token: string,
    code: string,
  ): Promise<{ method: MfaMethod; recovery_codes: string[] }> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/mfa/enroll/confirm`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify({ code }) });
    return handleResponse(response);
  },

  async mfaEnrollEmail(token: string): Promise<{ method: MfaMethod; enabled: boolean }> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/mfa/enroll/email`, {
      method: 'POST',
      headers: getAuthHeaders(token) });
    return handleResponse(response);
  },

  async mfaDisable(token: string, password: string): Promise<{ enabled: boolean }> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/mfa/disable`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify({ password }) });
    return handleResponse(response);
  },

  async mfaRegenerateRecoveryCodes(
    token: string,
    password: string,
  ): Promise<{ recovery_codes: string[] }> {
    const response = await authFetch(
      `${API_BASE_URL}/api/v1/auth/mfa/recovery-codes/regenerate`,
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ password }) },
    );
    return handleResponse(response);
  },

  async mfaTrustedDevices(token: string): Promise<MfaTrustedDevice[]> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/mfa/trusted-devices`, {
      method: 'GET',
      headers: getAuthHeaders(token) });
    return handleResponse<MfaTrustedDevice[]>(response);
  },

  async mfaRevokeTrustedDevice(token: string, deviceId: string): Promise<{ revoked: boolean }> {
    const response = await authFetch(
      `${API_BASE_URL}/api/v1/auth/mfa/trusted-devices/${deviceId}`,
      { method: 'DELETE', headers: getAuthHeaders(token) },
    );
    return handleResponse(response);
  },

  async mfaResetUser(token: string, userId: string): Promise<{ reset: boolean }> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/mfa/reset/${userId}`, {
      method: 'POST',
      headers: getAuthHeaders(token) });
    return handleResponse(response);
  },

  async checkRegistration(data: CheckRegistrationRequest): Promise<CheckRegistrationResponse> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/check-registration`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data) });
    return handleResponse<CheckRegistrationResponse>(response);
  },

  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await authFetch(`${API_BASE_URL}${API_ENDPOINTS.auth.register}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data) });
    return handleResponse<RegisterResponse>(response);
  },

  async logout(token: string): Promise<void> {
    await authFetch(`${API_BASE_URL}${API_ENDPOINTS.auth.logout}`, {
      method: 'POST',
      headers: getAuthHeaders(token) });
  },

  async getMe(token: string): Promise<User> {
    const response = await authFetch(`${API_BASE_URL}${API_ENDPOINTS.auth.me}`, {
      method: 'GET',
      headers: getAuthHeaders(token) });
    return handleResponse<User>(response);
  },

  async updateMe(token: string, data: UpdateUserData): Promise<User> {
    const response = await authFetch(`${API_BASE_URL}${API_ENDPOINTS.auth.me}`, {
      method: 'PUT',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data) });
    return handleResponse<User>(response);
  },

  // NOTE: there is deliberately no `refresh()` here. The ONE `/auth/refresh`
  // exchange is `refreshAccessTokens()` in `shared/auth/tokenRefresh.ts`, which
  // is single-flight: a second entry point would let two callers present the
  // same rotating refresh token to two exchanges. This one existed and did
  // exactly that until the AuthContext bootstrap was joined to the shared
  // exchange; don't add it back.

  async verifyEmail(data: VerifyEmailRequest): Promise<VerifyEmailResponse> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/verify-email`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data) });
    return handleResponse<VerifyEmailResponse>(response);
  },

  async resendVerification(data: ResendVerificationRequest): Promise<ResendVerificationResponse> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/resend-verification`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data) });
    return handleResponse<ResendVerificationResponse>(response);
  },

  async forgotPassword(data: { email: string }): Promise<{ message: string }> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/forgot-password`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data) });
    return handleResponse<{ message: string }>(response);
  },

  async resetPassword(data: { token: string; new_password: string }): Promise<{ message: string }> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/reset-password`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data) });
    return handleResponse<{ message: string }>(response);
  },

  async verifyResetToken(data: { token: string }): Promise<{ valid: boolean; email: string | null }> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/verify-reset-token`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data) });
    return handleResponse<{ valid: boolean; email: string | null }>(response);
  },

  async changePassword(
    token: string,
    data: { current_password: string; new_password: string }
  ): Promise<{ message: string }> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/change-password`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data) });
    return handleResponse<{ message: string }>(response);
  },

  // SSO Methods
  async ssoInit(provider: string, redirectUri: string, stateExtra?: Record<string, string>): Promise<{ auth_url: string; state: string }> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/sso/${provider}/init`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ redirect_uri: redirectUri, ...(stateExtra ? { state_extra: stateExtra } : {}) }) });
    return handleResponse<{ auth_url: string; state: string }>(response);
  },

  async ssoCallback(
    provider: string,
    code: string,
    redirectUri: string,
    sourceDomain?: string | null,
    userData?: Record<string, unknown> | null,
    state?: string | null,
  ): Promise<SSOCallbackResponse> {
    const body: Record<string, unknown> = { code, redirect_uri: redirectUri };
    if (sourceDomain) {
      body.source_domain = sourceDomain;
    }
    // Apple-specific: first-sign-in name payload, returned by Apple only once.
    if (userData) {
      body.user_data = userData;
    }
    // Raw OAuth state — the backend validates its server-issued, single-use
    // nonce (the authoritative cross-domain CSRF defense).
    if (state) {
      body.state = state;
    }
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/sso/${provider}/callback`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body) });
    return handleResponse<SSOCallbackResponse>(response);
  },

  /**
   * Native-app SSO callback (currently only used by iOS Sign in with Apple).
   * The native flow yields an identity token directly from Apple's
   * AuthenticationServices framework — no OAuth code exchange.
   */
  async ssoNativeCallback(
    provider: string,
    identityToken: string,
    audience: string,
    userData?: Record<string, unknown> | null,
  ): Promise<SSOCallbackResponse> {
    const body: Record<string, unknown> = {
      identity_token: identityToken,
      audience,
    };
    if (userData) {
      body.user_data = userData;
    }
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/sso/${provider}/native-callback`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body) });
    return handleResponse<SSOCallbackResponse>(response);
  },

  async ssoLink(
    userId: string,
    password: string,
    ssoInfo: SSOUserInfo
  ): Promise<LoginResponse | MfaChallengeResponse> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/sso/link`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ user_id: userId, password, sso_info: ssoInfo }) });
    return handleResponse<LoginResponse | MfaChallengeResponse>(response);
  },

  /**
   * The identity (email / provider / provider_id) is resolved server-side from
   * `registrationTicket` — minted by the SSO callback, single-use, and bound to
   * an httpOnly cookie in this browser. Only the display name, organization
   * name, consents and language are client-supplied.
   */
  async ssoCompleteRegistration(
    registrationTicket: string,
    firstName: string,
    lastName: string,
    companyName: string | undefined,
    joinTenantId: string | undefined,
    joinCompanyId: string | undefined,
    invitationToken: string | undefined,
    termsAccepted: boolean,
    contentProcessingConsent: boolean,
    /** Explicit login-screen language choice only — never the ambient UI language (backend defaults to 'en'). */
    preferredLanguage?: string,
    // `company` is present only in create-new-tenant mode. The joiner modes
    // (invitation / domain match) return a tenant-less `pending_setup` user that
    // ProtectedRoute routes to /setup, where the wizard's Flow B admits them —
    // so there is no tenant, and no company, to report yet.
    //
    // `pending_approval` is set (with NO tokens) when the sysadmin tenant-approval
    // gate holds a create-new-tenant signup for review — the caller shows the
    // "application received" screen instead of establishing a session, mirroring
    // the setup wizard's create path.
  ): Promise<LoginResponse & {
    company?: Record<string, unknown>;
    pending_approval?: boolean;
    message_code?: string;
  }> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/sso/complete-registration`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        registration_ticket: registrationTicket,
        first_name: firstName,
        last_name: lastName,
        company_name: companyName,
        join_tenant_id: joinTenantId,
        join_company_id: joinCompanyId,
        invitation_token: invitationToken,
        terms_accepted: termsAccepted,
        content_processing_consent: contentProcessingConsent,
        preferred_language: preferredLanguage,
      }) });
    return handleResponse<LoginResponse & {
      company?: Record<string, unknown>;
      pending_approval?: boolean;
      message_code?: string;
    }>(response);
  },


  // Register with invitation (join existing tenant). The server returns the same
  // user serialization as /login, so the caller can seed the session directly.
  async registerWithInvitation(data: RegisterWithInvitationRequest): Promise<AuthSessionResponse> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/register-with-invitation`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data) });
    return handleResponse<AuthSessionResponse>(response);
  },

  // Cross-domain SSO methods
  async exchangeCrossDomainCode(
    code: string,
  ): Promise<LoginResponse & { emulation_session?: Record<string, unknown> | null }> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/cross-domain/exchange`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ code }) });
    return handleResponse<LoginResponse & { emulation_session?: Record<string, unknown> | null }>(response);
  },

  async createCrossDomainCode(
    targetDomain: string,
    accessToken: string,
    refreshToken: string,
    user: User,
    emulationSession?: Record<string, unknown>,
  ): Promise<{ code: string }> {
    const response = await authFetch(`${API_BASE_URL}/api/v1/auth/cross-domain/create-code`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        target_domain: targetDomain,
        access_token: accessToken,
        refresh_token: refreshToken,
        user,
        ...(emulationSession ? { emulation_session: emulationSession } : {}),
      }) });
    return handleResponse<{ code: string }>(response);
  },

  /**
   * Build the MAIN-domain `GET /cross-domain/init` URL for cross-domain SSO login.
   *
   * The custom-domain SPA navigates the browser top-level to this main-domain URL
   * (instead of POSTing `/sso/init` on its own origin) so the per-flow httpOnly
   * state-binding cookie the backend plants lands on the MAIN domain — the only
   * place the cross-domain callback can read it (login-CSRF / session-fixation
   * defense). The endpoint mints the OAuth state and 302-redirects to the IdP.
   */
  buildCrossDomainInitUrl(
    provider: string,
    redirectUri: string,
    targetDomain: string,
    stateExtra?: Record<string, string>,
  ): string {
    const url = new URL(`${getMainDomainOrigin()}/api/v1/auth/cross-domain/init`);
    url.searchParams.set('target', targetDomain);
    url.searchParams.set('provider', provider);
    url.searchParams.set('redirect_uri', redirectUri);
    if (stateExtra?.invitation_token) {
      url.searchParams.set('invitation_token', stateExtra.invitation_token);
      if (stateExtra.invitation_tenant_id) {
        url.searchParams.set('invitation_tenant_id', stateExtra.invitation_tenant_id);
      }
      if (stateExtra.invitation_tenant_name) {
        url.searchParams.set('invitation_tenant_name', stateExtra.invitation_tenant_name);
      }
    }
    return url.toString();
  },
};

// SSO Types
/**
 * The only SSO identity fields a not-yet-registered browser is given. The
 * verified identity (provider_id, email_verified, is_email_relay) stays on the
 * server behind the single-use `registration_ticket` — see
 * `modules/user_management/services/sso_registration_ticket_service.py`.
 */
export interface SSODisplayInfo {
  provider: string;
  email: string;
  first_name: string;
  last_name: string;
  picture_url?: string;
}

/** Full identity, returned only on `link_required` (the account already exists). */
export interface SSOUserInfo extends SSODisplayInfo {
  provider_id: string;
  email_verified?: boolean;
  /** True for Apple privacy-relay addresses (xxx@privaterelay.appleid.com). */
  is_email_relay?: boolean;
}

export interface SSOCallbackResponse {
  // For authenticated / link_required / registration_required flows:
  status?: 'authenticated' | 'link_required' | 'registration_required';
  // For MFA challenge (no status, has challenge_token):
  challenge_token?: string;
  mfa_required?: boolean;
  mfa_enrollment_required?: boolean;
  mfa_method?: string;
  allowed_methods?: string[];
  // For authenticated:
  user?: User;
  access_token?: string;
  refresh_token?: string;
  redirect_to_domain?: string;
  // For link_required:
  email?: string;
  user_id?: string;
  has_password?: boolean;
  /** Full `SSOUserInfo` on `link_required`; display-only on `registration_required`. */
  sso_info?: SSOUserInfo | SSODisplayInfo;
  // For registration_required:
  /** Single-use, browser-bound handle to the server-verified identity. */
  registration_ticket?: string;
  registration_options?: RegistrationOption[];
}

export { AuthApiError };
